import * as React from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  Camera,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  Eye,
  Trash2,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import { Button, RowIconAction } from "../../components/ui-v2/button";
import { Card, CardContent } from "../../components/ui-v2/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui-v2/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui-v2/alert-dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../components/ui-v2/table";
import { Label, Text, MonoText } from "../../components/ui-v2/typography";
import { CurrencyValue, PercentValue } from "../../components/ui-v2/value-display";
import {
  CHART_ACCENT_COLOR,
  chartGradientStops,
  chartAxisTickStyle,
  getChartAnimationProps,
} from "../../components/ui-v2/chart-theme";
import { ChartTooltipShell, ChartTooltipRow } from "../../components/ui-v2/chart-tooltip";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { API_BASE_URL } from "../../lib/api";

/**
 * Snapshots Page — Institutional Console redesign (issue #237,
 * DESIGN-SPEC.md §5 "Snapshots").
 *
 * Layout: breadcrumb from Dashboard (in the toolbar's secondary-actions
 * slot, matching Snapshots.dc.html's "Dashboard / Snapshots" trail) →
 * sparkline header card ("Net worth across snapshots" area+line chart with
 * a trailing % change) → snapshot table (view/delete row actions). Full
 * page-level functionality (create snapshot, view details dialog, delete
 * confirmation) is retained 1:1 from the pre-redesign implementation —
 * only presentation changed.
 */

// Helper to format dates
function formatDate(dateString) {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatCompactDate(dateString) {
  const date = new Date(dateString + "T00:00:00");
  return date
    .toLocaleDateString("en-US", { month: "short", day: "2-digit" })
    .toUpperCase();
}

// Calculate change between two values
function calculateChange(current, previous) {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

/**
 * NetWorthSparklineCard — §5 "sparkline header card": area+line chart of
 * net worth across all loaded snapshots, with a trailing overall % change
 * label (Snapshots.dc.html: "+8.79% over 90 days").
 */
function NetWorthSparklineCard({ snapshots }) {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Snapshots arrive newest-first (API ordering); chart wants oldest-first.
  const chronological = React.useMemo(() => [...snapshots].reverse(), [snapshots]);

  const chartData = React.useMemo(
    () =>
      chronological.map((snapshot) => ({
        date: snapshot.date,
        label: formatCompactDate(snapshot.date),
        netWorth: snapshot.netWorth ?? snapshot.totalValue ?? 0,
      })),
    [chronological]
  );

  const overallChange = React.useMemo(() => {
    if (chronological.length < 2) return null;
    const first = chronological[0];
    const last = chronological[chronological.length - 1];
    return calculateChange(
      last.netWorth ?? last.totalValue,
      first.netWorth ?? first.totalValue
    );
  }, [chronological]);

  const daySpan = React.useMemo(() => {
    if (chronological.length < 2) return null;
    const first = new Date(`${chronological[0].date}T00:00:00`);
    const last = new Date(`${chronological[chronological.length - 1].date}T00:00:00`);
    return Math.round((last - first) / (1000 * 60 * 60 * 24));
  }, [chronological]);

  // Show a representative subset of x-axis labels (first, ~quartiles, last)
  // rather than every tick, matching the mockup's 5-label spread.
  const tickIndexes = React.useMemo(() => {
    const n = chartData.length;
    if (n === 0) return [];
    if (n <= 5) return chartData.map((_, i) => i);
    const step = (n - 1) / 4;
    return [0, 1, 2, 3, 4].map((i) => Math.round(i * step));
  }, [chartData]);

  return (
    <Card>
      <CardContent className="px-[18px] py-4">
        <div className="flex items-center justify-between">
          <span className="text-[13.5px] font-semibold text-foreground">
            Net worth across snapshots
          </span>
          {overallChange !== null && (
            <MonoText
              size="sm"
              className={cn(
                "font-normal",
                overallChange >= 0 ? "text-success" : "text-destructive"
              )}
            >
              {overallChange >= 0 ? "+" : ""}
              {overallChange.toFixed(2)}%{daySpan !== null ? ` over ${daySpan} days` : ""}
            </MonoText>
          )}
        </div>

        <div className="mt-3 h-[150px]">
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                <defs>
                  <linearGradient id="snapshotsNetWorthGradient" x1="0" y1="0" x2="0" y2="1">
                    {chartGradientStops.map((stop) => (
                      <stop
                        key={stop.offset}
                        offset={stop.offset}
                        stopColor={stop.stopColor}
                        stopOpacity={stop.stopOpacity}
                      />
                    ))}
                  </linearGradient>
                </defs>
                <RechartsTooltip
                  content={({ active, payload }) => {
                    const point = payload?.[0]?.payload;
                    if (!point) return null;
                    return (
                      <ChartTooltipShell active={active} payload={payload} label={point.label}>
                        <ChartTooltipRow
                          label="Net worth"
                          value={new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(point.netWorth)}
                          swatchColor={CHART_ACCENT_COLOR}
                        />
                      </ChartTooltipShell>
                    );
                  }}
                />
                <Area
                  type="linear"
                  dataKey="netWorth"
                  stroke={CHART_ACCENT_COLOR}
                  strokeWidth={2}
                  fill="url(#snapshotsNetWorthGradient)"
                  dot={false}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                  {...getChartAnimationProps(prefersReducedMotion)}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <Text variant="secondary" size="sm">
                Not enough snapshots yet to chart a trend
              </Text>
            </div>
          )}
        </div>

        {chartData.length > 1 && (
          <div className="mt-2 flex justify-between font-mono text-[10px] text-text-faint">
            {tickIndexes.map((index) => (
              <span key={`${chartData[index].date}-${index}`} style={{ fontFamily: chartAxisTickStyle.fontFamily }}>
                {chartData[index].label}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Snapshot row component
function SnapshotRow({ snapshot, previousSnapshot, onViewDetails, onDelete }) {
  const change = previousSnapshot
    ? calculateChange(
        snapshot.netWorth || snapshot.totalValue,
        previousSnapshot.netWorth || previousSnapshot.totalValue
      )
    : null;

  const isPositive = change > 0;
  const isNegative = change < 0;

  return (
    <TableRow className="cursor-pointer" onClick={() => onViewDetails(snapshot)}>
      <TableCell>
        <MonoText size="sm" className="whitespace-nowrap text-text-secondary">
          {formatDate(snapshot.date)}
        </MonoText>
      </TableCell>
      <TableCell className="text-right">
        <CurrencyValue
          value={snapshot.netWorth || snapshot.totalValue}
          currency="EUR"
          size="sm"
        />
      </TableCell>
      <TableCell className="text-right">
        <CurrencyValue value={snapshot.totalAssets} currency="EUR" size="sm" className="font-normal text-text-secondary" />
      </TableCell>
      <TableCell className="text-right">
        <CurrencyValue
          value={snapshot.totalLiabilities || 0}
          currency="EUR"
          size="sm"
          className="font-normal text-text-secondary"
        />
      </TableCell>
      <TableCell className="text-right">
        {change !== null ? (
          <div className="flex items-center justify-end gap-1">
            {isPositive ? (
              <TrendingUp className="h-3.5 w-3.5 text-success" aria-hidden="true" />
            ) : isNegative ? (
              <TrendingDown className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
            ) : (
              <Minus className="h-3.5 w-3.5 text-text-faint" aria-hidden="true" />
            )}
            <PercentValue value={change} size="sm" showSign showTrend={false} />
          </div>
        ) : (
          <MonoText size="sm" className="text-text-faint">
            &ndash;
          </MonoText>
        )}
      </TableCell>
      <TableCell className="w-20">
        <div className="flex items-center justify-end gap-1">
          <RowIconAction
            intent="neutral"
            label="View details"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(snapshot);
            }}
          >
            <Eye />
          </RowIconAction>
          <RowIconAction
            intent="negative"
            label="Delete snapshot"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(snapshot);
            }}
          >
            <Trash2 />
          </RowIconAction>
        </div>
      </TableCell>
    </TableRow>
  );
}

// Snapshot details modal
function SnapshotDetailsDialog({ snapshot, open, onOpenChange }) {
  if (!snapshot) return null;

  const categories = snapshot.categories || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Snapshot Details
          </DialogTitle>
          <DialogDescription>
            Portfolio snapshot from{" "}
            {new Date(snapshot.date + "T00:00:00").toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-3">
              <Label className="text-xs text-muted-foreground">Net Worth</Label>
              <CurrencyValue
                value={snapshot.netWorth || snapshot.totalValue}
                currency="EUR"
                size="lg"
                className="block mt-1"
              />
            </div>
            <div className="rounded-lg border border-border p-3">
              <Label className="text-xs text-muted-foreground">Total Assets</Label>
              <CurrencyValue
                value={snapshot.totalAssets}
                currency="EUR"
                size="lg"
                className="block mt-1"
              />
            </div>
          </div>

          {snapshot.totalLiabilities > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <Label className="text-xs text-muted-foreground">
                Total Liabilities
              </Label>
              <CurrencyValue
                value={-snapshot.totalLiabilities}
                currency="EUR"
                size="lg"
                colorize
                className="block mt-1"
              />
            </div>
          )}

          {/* Category Breakdown */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Asset Breakdown
            </Label>
            <div className="space-y-1.5">
              {Object.entries(categories)
                .filter(([key, cat]) => key !== "liabilities" && cat.value > 0)
                .sort((a, b) => b[1].value - a[1].value)
                .map(([key, category]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50"
                  >
                    <Text size="sm" className="capitalize">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </Text>
                    <div className="flex items-center gap-3">
                      <MonoText size="xs" className="text-muted-foreground">
                        {category.percentage?.toFixed(1)}%
                      </MonoText>
                      <CurrencyValue
                        value={category.value}
                        currency="EUR"
                        size="sm"
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="text-xs text-muted-foreground border-t border-border pt-2 mt-2">
            Created: {new Date(snapshot.createdAt).toLocaleString()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Breadcrumb - "Dashboard / Snapshots" trail (§5, Snapshots.dc.html toolbar).
 * Rendered via AppLayout's `secondaryActions` slot (left of the primary
 * action) since PageToolbar's `title` prop only accepts a plain string.
 */
function Breadcrumb() {
  return (
    <Link
      to="/"
      className="mr-2 flex items-center gap-1.5 text-[12.5px] text-muted-token transition-colors duration-[120ms] ease-out hover:text-foreground"
    >
      Dashboard
      <span className="text-[var(--chart-neutral-1,var(--color-text-tertiary))]" aria-hidden="true">/</span>
    </Link>
  );
}

export function Snapshots() {
  const location = useLocation();
  const [snapshots, setSnapshots] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [createSuccess, setCreateSuccess] = React.useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = React.useState(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [deleteSnapshot, setDeleteSnapshot] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  // Fetch snapshots
  const fetchSnapshots = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/snapshots`, {
        withCredentials: true,
      });

      setSnapshots(response.data.snapshots || []);
    } catch (err) {
      console.error("Error fetching snapshots:", err);
      setError(err.response?.data?.error || "Failed to load snapshots");
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new snapshot
  const createSnapshot = async () => {
    try {
      setCreating(true);
      setCreateSuccess(false);
      setError(null);

      await axios.post(
        `${API_BASE_URL}/snapshots/trigger`,
        {},
        { withCredentials: true }
      );

      setCreateSuccess(true);
      await fetchSnapshots();
      setTimeout(() => setCreateSuccess(false), 3000);
    } catch (err) {
      console.error("Error creating snapshot:", err);
      setError(err.response?.data?.error || "Failed to create snapshot");
    } finally {
      setCreating(false);
    }
  };

  // View snapshot details
  const handleViewDetails = (snapshot) => {
    setSelectedSnapshot(snapshot);
    setDetailsOpen(true);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (snapshot) => {
    setDeleteSnapshot(snapshot);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deleteSnapshot) return;

    try {
      setDeleting(true);
      setError(null);

      await axios.delete(
        `${API_BASE_URL}/snapshots/${deleteSnapshot.date}`,
        { withCredentials: true }
      );

      await fetchSnapshots();
      setDeleteDialogOpen(false);
      setDeleteSnapshot(null);
    } catch (err) {
      console.error("Error deleting snapshot:", err);
      setError(err.response?.data?.error || "Failed to delete snapshot");
    } finally {
      setDeleting(false);
    }
  };

  // Initial fetch
  React.useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots, location.key]);

  const primaryAction = (
    <Button onClick={createSnapshot} disabled={creating}>
      {creating ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <Camera className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {creating ? "Creating..." : "Create snapshot"}
    </Button>
  );

  return (
    <AppLayout title="Snapshots" secondaryActions={<Breadcrumb />} primaryAction={primaryAction}>
      <div className="mx-auto flex max-w-[1240px] flex-col gap-3.5">
        {/* Success/Error messages */}
        {createSuccess && (
          <div className="rounded-lg border border-success/20 bg-success/10 px-3 py-2">
            <Text size="sm" className="text-success">
              Snapshot created successfully!
            </Text>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2">
            <Text size="sm" className="text-destructive">
              {error}
            </Text>
          </div>
        )}

        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
            </CardContent>
          </Card>
        ) : snapshots.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Camera className="mx-auto mb-3 h-12 w-12 text-muted-foreground" aria-hidden="true" />
              <Text variant="secondary" size="lg">
                No snapshots yet
              </Text>
              <Text variant="secondary" size="sm" className="mt-1">
                Create your first snapshot to start tracking portfolio history
              </Text>
            </CardContent>
          </Card>
        ) : (
          <>
            <NetWorthSparklineCard snapshots={snapshots} />

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Net worth</TableHead>
                        <TableHead className="text-right">Assets</TableHead>
                        <TableHead className="text-right">Liabilities</TableHead>
                        <TableHead className="text-right">Change</TableHead>
                        <TableHead className="w-20 text-right" aria-label="Actions" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapshots.map((snapshot, index) => (
                        <SnapshotRow
                          key={snapshot.date}
                          snapshot={snapshot}
                          previousSnapshot={snapshots[index + 1]}
                          onViewDetails={handleViewDetails}
                          onDelete={handleDeleteClick}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Snapshot details dialog */}
      <SnapshotDetailsDialog
        snapshot={selectedSnapshot}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Snapshot</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the snapshot from{" "}
              <strong>
                {deleteSnapshot &&
                  new Date(deleteSnapshot.date + "T00:00:00").toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }
                  )}
              </strong>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

export default Snapshots;
